import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Building2, Globe, ArrowLeft, ExternalLink, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ExhibitionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exhibition, setExhibition] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/exhibitions/${id}`)
      .then(r => setExhibition(r.data))
      .catch(() => navigate("/exhibitions"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!exhibition) return null;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Navbar */}
      <nav className="border-b border-gray-200 bg-gray-50/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <Calendar className="h-6 w-6 text-blue-500" />
            <span className="text-xl font-bold">ExpoPulse <span className="text-blue-500">Events</span></span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate("/exhibitions")} className="text-gray-600 hover:text-gray-900 transition text-sm">Exhibitions</button>
            <button onClick={() => navigate("/organizers")} className="text-gray-600 hover:text-gray-900 transition text-sm">Organizers</button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 hover:text-gray-900 mb-6 text-sm transition">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {/* Hero image */}
        {exhibition.image_url && (
          <div className="h-64 rounded-xl overflow-hidden mb-8 bg-white">
            <img src={exhibition.image_url} alt={exhibition.name} className="w-full h-full object-cover" onError={(e) => e.target.style.display='none'} />
          </div>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold mb-4">{exhibition.name}</h1>

        {exhibition.industry && (
          <span className="inline-block bg-blue-50 text-blue-600 text-sm px-3 py-1 rounded mb-6">
            {exhibition.industry}
          </span>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {exhibition.venue && (
            <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <MapPin className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-gray-500">Venue</div>
                <div>{exhibition.venue}</div>
                <div className="text-sm text-gray-500">{[exhibition.city, exhibition.country].filter(Boolean).join(", ")}</div>
              </div>
            </div>
          )}

          {exhibition.start_date && (
            <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <Calendar className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-gray-500">Dates</div>
                <div>{exhibition.start_date}{exhibition.end_date ? ` — ${exhibition.end_date}` : ""}</div>
              </div>
            </div>
          )}

          {exhibition.organizer_name && (
            <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <Building2 className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-gray-500">Organizer</div>
                <div>{exhibition.organizer_name}</div>
              </div>
            </div>
          )}

          {exhibition.website && (
            <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-4">
              <Globe className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm text-gray-500">Website</div>
                <a href={exhibition.website} target="_blank" rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  Visit website <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        {exhibition.description && (
          <div className="mb-8">
            <h2 className="text-base font-semibold mb-3">About</h2>
            <p className="text-gray-600 leading-relaxed">{exhibition.description}</p>
          </div>
        )}

        {/* Exhibitors */}
        {exhibition.exhibitors?.length > 0 && (
          <div>
            <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Exhibitors ({exhibition.exhibitors.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {exhibition.exhibitors.map((ex) => (
                <div key={ex._id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="font-medium">{ex.name}</div>
                  {ex.country && <div className="text-sm text-gray-500">{ex.country}</div>}
                  {ex.booth_number && <div className="text-xs text-gray-400">Booth: {ex.booth_number}</div>}
                  {ex.website && (
                    <a href={ex.website} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1 mt-1">
                      Website <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
